import React, { useState, useMemo } from 'react';
import SEOHead from '../../components/SEOHead';
import Header from '../../components/ui/UnifiedHeader';
import SharedFooter from '../../components/SharedFooter';
import Icon from '../../components/AppIcon';
import { EnhancedBreadcrumb } from '../../components/ui/breadcrumb.jsx';
import { usePageMeta } from '../../components/BreadcrumbProvider.jsx';

import CalculatorCard from './components/CalculatorCard';
import StackedCalculatorCard from './components/StackedCalculatorCard';
import ROICalculatorForm from './components/ROICalculatorForm';
import EnhancedROICalculatorForm from './components/EnhancedROICalculatorForm';
import ROICalculatorSelector from './components/ROICalculatorSelector';
import ROIResults from './components/ROIResults';
import InstantProjectScoping from './components/InstantProjectScoping';
import LocationComparison from './components/LocationComparison';
import TalentAvailability from './components/TalentAvailability';
import ComplianceCostEstimator from './components/ComplianceCostEstimator';

const RoiCalculatorHubInteractiveDecisionTools = () => {
  const [activeCalculator, setActiveCalculator] = useState(null);
  const [calculatorMode, setCalculatorMode] = useState(null);
  const [calculationResults, setCalculationResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const breadcrumbs = useMemo(() => [
    { name: 'Home', href: '/' },
    { name: 'ROI Calculator', href: '/roi-calculator-hub-interactive-decision-tools' }
  ], []);

  // Set page metadata with breadcrumbs - memoize to prevent infinite loops
  const pageMeta = useMemo(() => ({
    title: 'ROI Calculator Hub - Interactive Decision Tools',
    breadcrumbs: breadcrumbs
  }), [breadcrumbs]);
  
  usePageMeta(pageMeta);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "ROI Calculator Hub - Interactive Decision Tools",
    "description": "Interactive ROI calculators and decision tools for Global Capability Center planning, cost estimation, and investment analysis.",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  // Calculator data for the cards
  const calculatorOptions = [
    {
      id: 'roi-basic',
      title: 'GCC ROI Calculator',
      description: 'Calculate your potential return on investment for establishing a Global Capability Center in India.',
      icon: 'Calculator',
      features: [
        'Cost savings analysis',
        'Timeline projections',
        'Risk assessment',
        'Customizable parameters'
      ],
      estimatedTime: '10-15 min',
      priority: 'high'
    },
    {
      id: 'location-comparison',
      title: 'Location Comparison Tool',
      description: 'Compare different Indian cities for your GCC setup based on talent, cost, and infrastructure.',
      icon: 'MapPin',
      features: [
        'Multi-city comparison',
        'Talent pool analysis',
        'Cost benchmarking',
        'Infrastructure scoring'
      ],
      estimatedTime: '8-12 min',
      priority: 'medium'
    },
    {
      id: 'talent-analysis',
      title: 'Talent Availability Analyzer',
      description: 'Analyze talent availability and skill sets across different locations and technologies.',
      icon: 'Users',
      features: [
        'Skill-based search',
        'Availability forecasting',
        'Salary benchmarking',
        'Competition analysis'
      ],
      estimatedTime: '6-10 min',
      priority: 'medium'
    },
    {
      id: 'compliance-estimator',
      title: 'Compliance Cost Estimator',
      description: 'Estimate regulatory compliance costs and requirements for your GCC operations.',
      icon: 'Shield',
      features: [
        'Regulatory mapping',
        'Cost estimation',
        'Timeline planning',
        'Risk identification'
      ],
      estimatedTime: '12-18 min',
      priority: 'low'
    }
  ];

  const handleCalculatorClick = (calculatorId) => {
    setActiveCalculator(calculatorId);
    setCalculatorMode(null);
    setCalculationResults(null);
    // Scroll to the form section
    document.getElementById('calculator-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleModeSelect = (mode) => {
    setCalculatorMode(mode);
  };

  const handleCalculate = async (formData) => {
    setIsCalculating(true);
    try {
      // Simulate API call with realistic delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      setCalculationResults(formData);
    } catch (error) {
      console.error('Calculation error:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  const handleScheduleConsultation = () => {
    // Navigate to consultation booking
    window.location.href = '/executive-contact-strategic-engagement';
  };

  return (
    <>
      <SEOHead
        title="ROI Calculator Hub - Interactive Decision Tools | Bootminds Elite"
        description="Interactive ROI calculators and decision tools for Global Capability Center planning. Calculate cost savings, project timelines, and investment returns for your GCC setup."
        keywords="ROI calculator, GCC cost calculator, investment analysis, decision tools, cost savings estimator, project timeline calculator"
        canonicalUrl="/calculator"
        breadcrumbs={breadcrumbs}
        schemaData={schemaData}
      />
      
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Breadcrumb Navigation */}
        <div className="bg-background border-b border-border">
          <div className="container-executive py-4">
            <EnhancedBreadcrumb items={breadcrumbs} />
          </div>
        </div>
        
        {/* Hero Section - Top Section */}
        <section className="py-16 lg:py-20 bg-gradient-to-b from-background to-background-alt content-animate">
          <div className="container-executive">
            <div className="max-w-4xl mx-auto text-center space-y-8">
              <div className="flex items-center justify-center space-x-4 hero-title-animate">
                <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center">
                  <Icon name="Calculator" size={40} color="var(--color-primary)" />
                </div>
                <div className="text-left">
                  <h1 className="font-headline font-bold text-4xl lg:text-5xl text-text-primary">
                    ROI Calculator Hub
                  </h1>
                  <p className="text-xl text-text-secondary mt-2">
                    Interactive Decision Tools
                  </p>
                </div>
              </div>
              
              <p className="text-lg text-text-secondary max-w-3xl mx-auto hero-description-animate">
                Make data-driven decisions for your Global Capability Center with our comprehensive suite of interactive calculators. 
                From ROI analysis to location comparison, get precise insights to guide your strategic planning.
              </p>

              <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-text-secondary hero-description-animate">
                <div className="flex items-center space-x-2">
                  <Icon name="TrendingUp" size={18} />
                  <span>Real-time Analysis</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Shield" size={18} />
                  <span>Data-driven Insights</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Target" size={18} />
                  <span>Customizable Parameters</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Download" size={18} />
                  <span>Exportable Reports</span>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 pt-8">
                <div className="text-center stats-animate">
                  <div className="text-3xl font-bold text-text-primary mb-2">~1,700</div>
                  <div className="text-text-secondary text-sm">India GCCs (NASSCOM 2024)</div>
                </div>
                <div className="text-center stats-animate">
                  <div className="text-3xl font-bold text-text-primary mb-2">50–60%</div>
                  <div className="text-text-secondary text-sm">Cost Savings vs US</div>
                </div>
                <div className="text-center stats-animate">
                  <div className="text-3xl font-bold text-text-primary mb-2">4</div>
                  <div className="text-text-secondary text-sm">Interactive Decision Tools</div>
                </div>
                <div className="text-center stats-animate">
                  <div className="text-3xl font-bold text-text-primary mb-2">$64.6B</div>
                  <div className="text-text-secondary text-sm">GCC Revenue FY24 (NASSCOM)</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Calculator Selection Section */}
        <section className="py-16 bg-card content-section-animate">
          <div className="container-executive">
            <div className="text-center mb-12">
              <h2 className="font-headline font-bold text-3xl lg:text-4xl text-text-primary mb-4">
                Choose Your Calculator
              </h2>
              <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                Select the right tool for your analysis needs. Each calculator is designed to provide specific insights 
                for different aspects of your GCC planning and decision-making process.
              </p>
            </div>

            {/* Featured Calculator */}
            <div className="mb-12">
              <StackedCalculatorCard
                title={calculatorOptions[0].title}
                description={calculatorOptions[0].description}
                icon={calculatorOptions[0].icon}
                features={calculatorOptions[0].features}
                isActive={activeCalculator === calculatorOptions[0].id}
                onClick={() => handleCalculatorClick(calculatorOptions[0].id)}
                completionRate={activeCalculator === calculatorOptions[0].id ? 25 : 0}
                estimatedTime={calculatorOptions[0].estimatedTime}
                priority={calculatorOptions[0].priority}
              />
            </div>

            {/* Other Calculators Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {calculatorOptions.slice(1).map((calculator) => (
                <CalculatorCard
                  key={calculator.id}
                  title={calculator.title}
                  description={calculator.description}
                  icon={calculator.icon}
                  features={calculator.features}
                  isActive={activeCalculator === calculator.id}
                  onClick={() => handleCalculatorClick(calculator.id)}
                  completionRate={activeCalculator === calculator.id ? 15 : 0}
                  estimatedTime={calculator.estimatedTime}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Interactive Calculator Section */}
        <section id="calculator-form" className="py-16 bg-background">
          <div className="container-executive">
            {activeCalculator && (
              <div className="max-w-6xl mx-auto space-y-8">
                <div className="text-center">
                  <h2 className="font-headline font-bold text-2xl lg:text-3xl text-text-primary mb-4">
                    {activeCalculator === 'roi-basic' ? 'GCC ROI Calculator' : 'Interactive Calculator'}
                  </h2>
                  <p className="text-text-secondary">
                    {activeCalculator === 'roi-basic' 
                      ? 'Get precise ROI calculations and cost savings analysis for your Global Capability Center setup.'
                      : 'Fill in your details below to get personalized analysis and insights.'
                    }
                  </p>
                </div>

                {/* ROI Calculator with Mode Selection */}
                {activeCalculator === 'roi-basic' && (
                  <>
                    {!calculatorMode && (
                      <ROICalculatorSelector 
                        onModeSelect={handleModeSelect}
                        selectedMode={calculatorMode}
                      />
                    )}
                    
                    {calculatorMode && (
                      <div className="space-y-8">
                        {/* Mode Toggle */}
                        <div className="flex items-center justify-center">
                          <button
                            onClick={() => {
                              setCalculatorMode(null);
                              setCalculationResults(null);
                            }}
                            className="flex items-center space-x-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                          >
                            <Icon name="ArrowLeft" size={16} />
                            <span>Back to calculator selection</span>
                          </button>
                        </div>

                        {/* Calculator Form */}
                        {calculatorMode === 'quick' ? (
                          <ROICalculatorForm 
                            onCalculate={handleCalculate}
                            isCalculating={isCalculating}
                          />
                        ) : (
                          <EnhancedROICalculatorForm 
                            onCalculate={handleCalculate}
                            isCalculating={isCalculating}
                          />
                        )}

                        {/* Results */}
                        {calculationResults && (
                          <ROIResults 
                            calculationData={calculationResults}
                            onScheduleConsultation={handleScheduleConsultation}
                          />
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Other Calculator Types */}
                {activeCalculator === 'location-comparison' && (
                  <LocationComparison />
                )}
                
                {activeCalculator === 'talent-analysis' && (
                  <TalentAvailability />
                )}
                
                {activeCalculator === 'compliance-estimator' && (
                  <ComplianceCostEstimator />
                )}
              </div>
            )}
            
            {!activeCalculator && (
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="Calculator" size={40} color="var(--color-text-secondary)" />
                </div>
                <h3 className="font-headline font-semibold text-xl text-text-primary mb-2">
                  Select a Calculator Above
                </h3>
                <p className="text-text-secondary max-w-md mx-auto">
                  Choose from our comprehensive suite of calculators to get started with your GCC planning and ROI analysis.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Additional Tools Section */}
        <section className="py-16 bg-card">
          <div className="container-executive">
            <div className="text-center mb-12">
              <h2 className="font-headline font-bold text-2xl lg:text-3xl text-text-primary mb-4">
                Additional Planning Tools
              </h2>
              <p className="text-text-secondary max-w-3xl mx-auto">
                Explore our comprehensive suite of planning and analysis tools to make informed decisions about your GCC setup.
              </p>
            </div>
            
            {/* Project scoping is unique to this section; the three comparison tools
                above render via card selection (no longer duplicated here). */}
            <div className="space-y-16">
              <InstantProjectScoping />
            </div>
          </div>
        </section>

        <SharedFooter />
      </div>
    </>
  );
};

export default RoiCalculatorHubInteractiveDecisionTools;